import { Maximize2, ZoomIn } from 'lucide-react';
import { useRef, useState } from 'react';

import { loadZoom } from '@/components/lazyUi';
import { ratioOf, rememberRatio } from '@/imageRatio';
import { haptic } from '@/platform';
import { useT } from '@/str';

type Zoom = typeof import('@/components/ZoomView').ZoomView;

const QIMG = 'block size-full bg-white object-contain';

const TAP_SLOP_PX = 10;

export function QuestionImage({
  src,
  priority = true,
  zoom = true,
  bleed = false,
}: {
  src: string | null;
  priority?: boolean;
  zoom?: boolean;
  bleed?: boolean;
}) {
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [Zoom, setZoom] = useState<Zoom | null>(null);
  const [zoomed, setZoomed] = useState(false);
  const zoomBtn = useRef<HTMLButtonElement>(null);
  const press = useRef<{ x: number; y: number } | null>(null);
  const open = () =>
    void loadZoom().then((view) => {
      if (!view) {
        haptic.notify('error');
        window.dispatchEvent(new Event('vite:preloadError'));
        return;
      }
      setZoom(() => view);
      setZoomed(true);
    });
  const [ratio, setRatio] = useState(() => (src ? ratioOf(src) : 0));
  const t = useT();

  if (!src) return null;

  if (bleed) {
    if (failedSrc === src) {
      return (
        <div
          role="alert"
          className="border-border bg-card -mx-[calc(var(--pad)-6px)] flex aspect-video w-[calc(100%+(var(--pad)-6px)*2)] items-center justify-center rounded-xs border p-3.5"
        >
          <div className="border-border-strong rounded-ctl flex size-full flex-col items-center justify-center gap-2.5 border-[1.5px] border-dashed text-center">
            <span className="text-t13 text-muted-foreground font-extrabold">{t.exp.imgFail}</span>
            <button
              type="button"
              onClick={() => {
                setFailedSrc(null);
                setAttempt((n) => n + 1);
              }}
              className="border-border-strong text-foreground text-t12h min-h-11 rounded-sm border px-4 font-extrabold"
            >
              {t.exp.retry}
            </button>
          </div>
        </div>
      );
    }
    return (
      <div
        data-qimg-frame
        className="border-border relative -mx-[calc(var(--pad)-6px)] w-[calc(100%+(var(--pad)-6px)*2)] overflow-hidden rounded-xs border bg-white"
        style={{ aspectRatio: ratio || 16 / 9 }}
      >
        <img
          key={attempt}
          data-qimg
          className="block size-full object-contain data-zoomable:cursor-zoom-in"
          src={src}
          alt=""
          loading={priority ? 'eager' : 'lazy'}
          decoding="async"
          fetchPriority={priority ? 'high' : 'auto'}
          onLoad={(e) => {
            const { naturalWidth, naturalHeight } = e.currentTarget;
            rememberRatio(src, naturalWidth, naturalHeight);
            if (naturalWidth > 0 && naturalHeight > 0) setRatio(naturalWidth / naturalHeight);
          }}
          onError={() => setFailedSrc(src)}
          onPointerDown={(e) => {
            press.current = { x: e.clientX, y: e.clientY };
          }}
          onClick={(e) => {
            const from = press.current;
            press.current = null;
            if (!zoom || !from) return;
            if (Math.hypot(e.clientX - from.x, e.clientY - from.y) > TAP_SLOP_PX) return;
            open();
          }}
          data-zoomable={zoom || undefined}
        />
        {zoom && (
          <button
            type="button"
            ref={zoomBtn}
            aria-haspopup="dialog"
            aria-expanded={zoomed}
            aria-label={t.zoom}
            title={t.zoom}
            onClick={open}
            className="bg-background/90 text-foreground border-border rounded-ctl absolute right-2.5 bottom-2.5 flex size-8.5 items-center justify-center border after:absolute after:-inset-1.5 after:content-['']"
          >
            <ZoomIn size={17} aria-hidden />
          </button>
        )}
        {Zoom && <Zoom src={src} open={zoomed} onOpenChange={setZoomed} trigger={zoomBtn} />}
      </div>
    );
  }

  if (failedSrc === src) return null;

  return (
    <div
      className="rounded-card border-edge relative mx-auto max-w-full overflow-hidden border"
      style={{ aspectRatio: ratio, width: `min(100%, calc(35vh * ${ratio}))` }}
    >
      <img
        data-qimg
        className={QIMG}
        src={src}
        alt=""
        loading={priority ? 'eager' : 'lazy'}
        decoding="async"
        fetchPriority={priority ? 'high' : 'auto'}
        onLoad={(e) => {
          const { naturalWidth, naturalHeight } = e.currentTarget;
          rememberRatio(src, naturalWidth, naturalHeight);
          if (naturalWidth > 0 && naturalHeight > 0) setRatio(naturalWidth / naturalHeight);
        }}
        onError={() => setFailedSrc(src)}
      />
      {zoom && (
        <button
          type="button"
          ref={zoomBtn}
          aria-haspopup="dialog"
          aria-expanded={zoomed}
          onClick={open}
          className="rounded-ctl absolute right-2.5 bottom-2.5 flex size-8.5 items-center justify-center border border-white/35 bg-black/55 text-white after:absolute after:-inset-1.5 after:content-['']"
          aria-label={t.zoom}
        >
          <Maximize2 size={15} aria-hidden />
        </button>
      )}
      {Zoom && <Zoom src={src} open={zoomed} onOpenChange={setZoomed} trigger={zoomBtn} />}
    </div>
  );
}
